import type { TaxRuleView } from "@cx/contracts";
import {
  formatPct,
  type BusinessBranch,
  type Crt,
  type IeIndicator,
  type RevenueBand,
  type TaxDirection,
  type TaxRegime,
  type TaxTotals,
  type WizardActivity,
} from "@cx/domain";

/** Rótulos das naturezas de operação, do assistente e dos impostos estimados (T2.2). */

export const DIRECTION_LABEL: Record<TaxDirection, string> = { out: "Saída", in: "Entrada" };

export const PURPOSE_LABEL: Record<number, string> = {
  1: "Normal",
  2: "Complementar",
  3: "Ajuste",
  4: "Devolução de mercadoria",
};

export const PRESENCE_LABEL: Record<number, string> = {
  0: "Não se aplica",
  1: "Presencial",
  2: "Internet",
  3: "Teleatendimento",
  4: "Entrega em domicílio (NFC-e)",
  5: "Presencial fora do estabelecimento",
  9: "Outros (não presencial)",
};

export const DOC_MODEL_LABEL: Record<string, string> = { "55": "NF-e (55)", "65": "NFC-e (65)" };

export const CRT_LABEL: Record<Crt, string> = {
  1: "1 – Simples Nacional",
  2: "2 – Simples Nacional, excesso de sublimite",
  3: "3 – Regime normal",
  4: "4 – MEI",
};

export const TAX_REGIME_LABEL: Record<TaxRegime, string> = {
  simples: "Simples Nacional",
  mei: "MEI",
  presumido: "Lucro Presumido",
  real: "Lucro Real",
};

export const REVENUE_BAND_LABEL: Record<RevenueBand, string> = {
  ate_180k: "Até R$ 180 mil",
  ate_360k: "De R$ 180 mil a R$ 360 mil",
  ate_720k: "De R$ 360 mil a R$ 720 mil",
  ate_1800k: "De R$ 720 mil a R$ 1,8 milhão",
  ate_3600k: "De R$ 1,8 milhão a R$ 3,6 milhões",
  ate_4800k: "De R$ 3,6 milhões a R$ 4,8 milhões",
  acima_4800k: "Acima de R$ 4,8 milhões",
};

export const BRANCH_LABEL: Record<BusinessBranch, string> = { comercio: "Comércio", industria: "Indústria" };

export const ACTIVITY_LABEL: Record<WizardActivity, { label: string; hint: string }> = {
  compra_venda: {
    label: "Compra e venda de mercadorias",
    hint: "Venda para revenda e para consumidor final.",
  },
  st: { label: "Mercadorias com substituição tributária", hint: "Revenda de produto com ICMS-ST já retido." },
  import_export: { label: "Exportação", hint: "Venda para cliente no exterior." },
  transferencia: {
    label: "Transferência entre filiais",
    hint: "Envio para outro estabelecimento da empresa.",
  },
  remessas: { label: "Remessas", hint: "Conserto, reparo e demonstração." },
  devolucao: { label: "Devoluções", hint: "Devolução de compra (saída) e de venda (entrada)." },
  bonificacao: { label: "Bonificação, doação ou brinde", hint: "Saída sem cobrança." },
};

export const IE_INDICATOR_LABEL: Record<IeIndicator, string> = {
  1: "Contribuinte do ICMS",
  2: "Contribuinte isento",
  9: "Não contribuinte",
};

const pct = (v: number | null) => (v === null ? null : formatPct(v));

/** Condições da regra em uma linha ("Todas as condições" quando é a regra geral). */
export function ruleConditionsText(r: TaxRuleView): string {
  const parts: string[] = [];
  if (r.destUf) parts.push(r.destUf === "EX" ? "Exterior" : `UF ${r.destUf}`);
  if (r.destIeIndicator !== null) parts.push(IE_INDICATOR_LABEL[r.destIeIndicator]);
  if (r.destFinalConsumer !== null) parts.push(r.destFinalConsumer ? "Consumidor final" : "Revenda");
  if (r.crt !== null) parts.push(`CRT ${r.crt}`);
  if (r.ncmPrefix) parts.push(`NCM ${r.ncmPrefix}${r.ncmPrefix.length < 8 ? "…" : ""}`);
  if (r.productOrigin !== null) parts.push(`Origem ${r.productOrigin}`);
  if (r.productId) parts.push("Produto específico");
  if (r.productGroupId) parts.push("Grupo de produtos");
  return parts.length ? parts.join(" · ") : "Todas as condições";
}

/** Tributação da regra em uma linha: CFOP, CST/CSOSN e alíquotas informadas. */
export function ruleTaxText(r: TaxRuleView): string {
  const parts = [`CFOP ${r.cfop}`];
  if (r.icmsCst) parts.push(`CST ${r.icmsCst}`);
  if (r.icmsCsosn) parts.push(`CSOSN ${r.icmsCsosn}`);
  const icms = pct(r.icmsPct4);
  if (icms) parts.push(`ICMS ${icms}`);
  if (r.icmsStMvaPct4 !== null) parts.push(`MVA ${pct(r.icmsStMvaPct4)}`);
  const ipi = pct(r.ipiPct4);
  if (ipi) parts.push(`IPI ${ipi}`);
  if (r.ibsCst) parts.push(`IBS/CBS ${r.ibsCst}`);
  return parts.join(" · ");
}

/** Linhas do quadro "Impostos estimados" (só os valores diferentes de zero), na ordem da DANFE. */
export function taxTotalsRows(t: TaxTotals): { label: string; cents: number }[] {
  const rows: [string, number][] = [
    ["Base de cálculo do ICMS", t.baseCents],
    ["ICMS", t.icmsCents],
    ["FCP", t.fcpCents],
    ["Base do ICMS ST", t.stBaseCents],
    ["ICMS ST", t.stCents],
    ["FCP ST", t.fcpStCents],
    ["DIFAL (UF de destino)", t.difalCents],
    ["FCP (UF de destino)", t.difalFcpCents],
    ["IPI", t.ipiCents],
    ["PIS", t.pisCents],
    ["COFINS", t.cofinsCents],
    ["Crédito de ICMS (Simples)", t.creditSnCents],
    ["IBS", t.ibsCents],
    ["CBS", t.cbsCents],
    ["Imposto seletivo", t.isCents],
  ];
  return rows.filter(([, v]) => v !== 0).map(([label, cents]) => ({ label, cents }));
}
